import axios from 'axios';

// Use VITE_API_URL if set (for Railway/staging), otherwise use relative path (for production with Nginx proxy)
const getBaseURL = () => {
  // Check for VITE_API_URL environment variable (set at build time)
  if (import.meta.env.VITE_API_URL) {
    // Ensure it ends with /api if not already
    const apiUrl = import.meta.env.VITE_API_URL;
    return apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
  }
  // Fallback to relative path for production (Nginx proxy)
  return '/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

console.log('API Client initialized with baseURL:', api.defaults.baseURL);

export function setAppClient(client) {
  if (typeof window !== 'undefined') {
    window.__APP_CLIENT__ = client;
  }
  if (api && api.defaults && api.defaults.headers) {
    api.defaults.headers.common['X-App-Client'] = client;
  }
}

// Request interceptor - add JWT token if available
api.interceptors.request.use(
  (config) => {
    // Set X-App-Client header to isolate admin and user sessions
    const appClient =
      (typeof window !== 'undefined' && window.__APP_CLIENT__) ||
      (typeof import.meta !== 'undefined' && import.meta.env?.VITE_APP_CLIENT) ||
      (typeof window !== 'undefined' && window.location.port === '6174'
        ? 'admin'
        : typeof window !== 'undefined' && window.location.port === '6173'
        ? 'user'
        : undefined);

    if (appClient && config.headers && !config.headers['X-App-Client']) {
      config.headers['X-App-Client'] = appClient;
    }

    // Let the browser set multipart boundary — default application/json breaks file uploads
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers) {
        if (typeof config.headers.delete === 'function') {
          config.headers.delete('Content-Type');
        } else {
          delete config.headers['Content-Type'];
        }
      }
    }

    // Try to get token from localStorage (if stored there)
    const token = localStorage.getItem('jwtToken') || localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const requestUrl = error.config?.url || '';
    const bypass401Redirect =
      requestUrl.includes('/investor/access/verify') || requestUrl.includes('/investor/access/set');

    // Handle network errors (no response from server)
    if (!error.response) {
      // Check if it's a network error
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        console.error('Network Error: Cannot connect to backend server');
        console.error('Backend URL:', error.config?.baseURL);
        console.error('Request URL:', error.config?.url);
        // Provide a more helpful error message
        error.userMessage = 'Cannot connect to server. Please check if the backend is running.';
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.error('Request timeout: Server took too long to respond');
        error.userMessage = 'Request timeout. Please try again.';
      } else {
        console.error('Unknown network error:', error.message);
        error.userMessage = error.message || 'Network error occurred';
      }
    }

    // Handle 401 responses - try to refresh token first
    if (error.response?.status === 401 && !bypass401Redirect) {
      console.log('🔐 API Client: Received 401 Unauthorized');
      console.log('🔐 Current path:', window.location.pathname);
      console.log('🔐 Request URL:', error.config?.url);
      console.log('🔐 Request method:', error.config?.method);
      
      // Prevent infinite retry loops - if this request has already been retried, don't retry again
      if (error.config?._retryCount >= 1) {
        console.log('🔐 Request already retried, preventing infinite loop');
        // Clear stored user data on final failure
        localStorage.removeItem('user');
        return Promise.reject(error);
      }
      
      // Mark this request as retried
      if (error.config) {
        error.config._retryCount = (error.config._retryCount || 0) + 1;
      }
      
      // Try to refresh the token first (but don't refresh the refresh endpoint itself)
      const isRefreshRequest = error.config?.url === '/auth/refresh' || error.config?.url?.includes('/auth/refresh');
      if (!isRefreshRequest) {
        try {
          console.log('🔐 Attempting to refresh JWT token...');
          // Refresh endpoint is GET, not POST
          await api.get('/auth/refresh');
          console.log('🔐 Token refreshed successfully, retrying original request...');

          // Retry the original request
          const originalRequest = error.config;
          return api(originalRequest);
        } catch (refreshError) {
          console.log('🔐 Token refresh failed:', refreshError.response?.status, refreshError.message);
          // If refresh failed, clear user data
          localStorage.removeItem('user');
          // Don't immediately redirect - let ProtectedRoute handle it after auth check
          return Promise.reject(error);
        }
      }

      // Don't automatically redirect on 401 - let ProtectedRoute handle navigation
      // This prevents kicking users out during browser back navigation
      console.log('🔐 401 error - letting ProtectedRoute handle navigation');
      
      // Clear any stored user data (but don't redirect)
      localStorage.removeItem('user');
      
      // Return the error so ProtectedRoute can handle it appropriately
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export default api;

